// Copyright 2024, Imprivata, Inc.  All rights reserved.

import {
  AppError,
  Authn,
  ErrorCode,
  type EventPayload,
  EventType,
  type FactorOption,
  FatalError,
  RetryableError,
} from '@imprivata-cloud/authn';
import {
  addSpanEvent,
  createSpanName,
  debug,
  error,
  instrument,
  SpanComponent,
  ImprHeaders,
} from '@imprivata-cloud/common';
import { WEB_APP_NAME } from '../../shared/constants';
import { QueryParam } from '../../constants';
import { handleAuthnArtifact, setDefaultHeader } from '../../api/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { jsonToBase64 } from '@imprivata-cloud/dataprivacy';
import {
  endJourneyAndCloseWindow,
  hideWindow,
  showWindow,
} from '../../api/agent';
import { getWorkflowId } from '../../tracing';
import {
  Card,
  type CardHeaderBannerProps,
  CardHeaders,
} from '@imprivata-cloud/components';
import {
  IGNORED_ERROR_CODES,
  IGNORED_ERROR_MESSAGES,
  mapErrorToStatus,
} from '../../utils/bannerUtils';
import { useTranslation } from 'react-i18next';
import OfflineIcon from '../../assets/offline.svg?react';


const onSuccessSpan = createSpanName(SpanComponent.UI, 'service', 'on-success');

export const IcpFlow: React.FC = () => {
  const { t } = useTranslation();

  const queryParams = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);
  const tenantId = useMemo(() => {
    return queryParams.get(QueryParam.TenantId) ?? '';
  }, [queryParams]);
  const rpRequestId = useMemo(() => {
    return queryParams.get(QueryParam.RpRequestId) ?? '';
  }, [queryParams]);
  const contextResource = useMemo(() => {
    return {
      resourceType: queryParams.get(QueryParam.ResourceType) ?? '',
      contextType: queryParams.get(QueryParam.ContextType) ?? '',
    };
  }, [queryParams]);
  const username = useMemo(() => {
    return queryParams.get(QueryParam.LoginHint) ?? '';
  }, [queryParams]);
  const oidcRequestData = useMemo(
    () => ({
      codeChallenge: queryParams.get(QueryParam.CodeChallenge) ?? '',
      codeChallengeMethod:
        queryParams.get(QueryParam.CodeChallengeMethod) ?? '',
    }),
    [queryParams],
  );
  const requestConfig = useMemo(
    () => ({
      clientName: WEB_APP_NAME,
      username,
      oidcRequestData,
    }),
    [oidcRequestData, username],
  );
  const [header, setHeader] = useState<CardHeaders>(CardHeaders.DEFAULT);
  const [showHexagon, setShowHexagon] = useState<boolean | undefined>(false);
  const [banner, setBanner] = useState<CardHeaderBannerProps | undefined>();

  const [finished, setFinished] = useState(false);

  useEffect(() => {
    setDefaultHeader(
      ImprHeaders.ImprClientName,
      jsonToBase64({
        name: WEB_APP_NAME,
      }),
    );
  }, []);

  useEffect(() => {
    setDefaultHeader(ImprHeaders.ImprTenantId, tenantId);
  }, [tenantId]);

  useEffect(() => {
    showWindow();
  }, []);

  // scroll the page up when virtual keyboard is hidden (input looses focus)
  useEffect(() => {
    // this fixes an issue on mobile when, after virtual keyboard is hidden, the page
    // remains scrolled down a bit and there is no way to scroll it up because overall
    // container has "overflow: hidden" and there is no scroll bar
    const scrollHandler = () => {
      window.scrollTo(0, 0);
    };
    // 'focusout' - is the same as 'blur' but with the difference that it bubbles up,
    // so that it, unlike 'blur', can be catched on 'window'
    window.addEventListener('focusout', scrollHandler);
    return () => {
      window.removeEventListener('focusout', scrollHandler);
    };
  }, []);

  const onSuccess = useCallback(
    instrument(onSuccessSpan, async (authorizationCode: string) => {
      try {
        debug('Processing authentication with code');

        const {
          redirectUri,
          authorizationCode: responseAuthorizationCode,
          state,
        } = await handleAuthnArtifact({
          rpRequestId,
          authorizationCode,
        });

        debug('Authentication artifact processed successfully', {
          redirectUri,
          hasState: !!state,
        });

        setFinished(true);
        hideWindow();

        const stateParam = state ? `&state=${state}` : '';

        addSpanEvent(createSpanName(onSuccessSpan, 'redirect'), {
          redirectUri,
          state,
        });

        debug('Redirecting to relying party', { redirectUri });

        window.location.href = `${redirectUri}?code=${responseAuthorizationCode}${stateParam}`;
      } catch (err) {
        error('Authentication failed', String(err));
        throw err; // Let the instrumentation handle the error
      }
    }),
    [],
  );

  const displayBanner = useCallback(
    (error: AppError) => {
      const noBanner =
        error instanceof FatalError ||
        (error.code && IGNORED_ERROR_CODES.includes(error.code)) ||
        (error.message && IGNORED_ERROR_MESSAGES.includes(error.message));

      if (noBanner) {
        setBanner(undefined);
        return;
      }

      const isOfflineError =
        error.code &&
        (error.code === ErrorCode.OFFLINE ||
          error.code === ErrorCode.NETWORK_ERROR);

      let customIcon = undefined;
      let subMessage = undefined;

      if (isOfflineError) {
        customIcon = () => <OfflineIcon />;
        subMessage = t('errors.offline-sub-message');
      } 

      const newBanner: CardHeaderBannerProps = {
        message: error.code ? t(`errors.${error.code}`) : t(error.message),
        status: mapErrorToStatus(error),
        customIcon,
        subMessage,
      };

      setBanner(newBanner);
    },
    [t],
  );

  // Handle events coming from AuthnModule
  const handleEvent = useCallback(
    ({ event, data }: EventPayload) => {
      debug('Received event', { event, data });
      createSpanName(SpanComponent.UI, 'event', `on-event--${event}`);
      if (event === EventType.AUTHENTICATED) {
        debug('Authentication successful');
        onSuccess(data?.oidcAuthorizationCode ?? '');
      } else if (event === EventType.CANCEL) {
        debug('Authentication canceled by user');
        endJourneyAndCloseWindow();
      }
      switch (event) {
        case EventType.ERROR:
          if (data) {
            displayBanner(data);
          }
          break;
        case EventType.START_ENROLLING:
          if (data) {
            setHeader(CardHeaders.GETTING_STARTED);
          } else {
            setHeader(CardHeaders.DEFAULT);
          }
          break;
        case EventType.START_AUTHENTICATING:
          setShowHexagon(data);
          break;
        case EventType.ONLINE:
        case EventType.RESTART_STATE:
          setBanner(undefined);
          break;
        case EventType.OFFLINE:
          displayBanner(new RetryableError({ code: ErrorCode.OFFLINE }));
          break;
        case EventType.IMPR_ID_ERROR:
        case EventType.INTERNAL_ERROR:
          if (data) {
            if (typeof data === 'string') {
              displayBanner(
                new AppError({
                  message: data,
                  code: ErrorCode.UNKNOWN,
                }),
              );
            } else {
              displayBanner(data);
            }
          }
          break;
        case EventType.CAMERA_IN_USE:
          if (data) {
            if (typeof data === 'string') {
              displayBanner(
                new AppError({ message: data, code: ErrorCode.CAMERA_IN_USE }),
              );
            } else {
              displayBanner(data);
            }
          }
          break;
      }
    },
    [displayBanner, onSuccess],
  );
  const tracing = useMemo(
    () => ({
      workflowId: getWorkflowId() ?? '',
    }),
    [],
  );
  const factors = useMemo(() => [{ factorType: 'face' } as FactorOption], []);
  const hideAddAnotherMethod = useMemo(() => true, []);
  if (finished) {
    return <></>;
  }

  return (
    <div className="authn-container">
      <Card banner={banner} hexImage={showHexagon} header={header}>
        <Authn
          contextResource={contextResource}
          tenantId={tenantId}
          onEvent={handleEvent}
          factors={factors}
          requestConfig={requestConfig}
          tracing={tracing}
          hideAddAnotherMethod={hideAddAnotherMethod}
        />
      </Card>
    </div>
  );
};
